import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Toolbar,
  Typography,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Button,
  Popover,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import CheckIcon from "@mui/icons-material/Check";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../globalState/hooks";
import dashboardServices from "../../services/DashboardServices";
import { onPaymentsEvent } from "../../services/paymentsEvents";
import {
  formatWalletDisplay,
  normalizeBalanceSummaryResponse,
} from "../../helper/formatAppBalance";
import { useWalletBalanceVisibility } from "../../hooks/useWalletBalanceVisibility";
import { NJIM } from "../../theme/njimTheme";
import { CURRENCY_SYMBOL } from "../../constants/currency";
import SupportContactLinks from "../../components/SupportContactLinks";
import { applyDocumentDirection } from "../../helper/documentDirection";
import { RTL_ICON_BUTTON_SX, popoverOriginEnd, rtlIconButtonProps } from "../../helper/rtlIconButton";

interface HeaderProps {
  onMenuClick: () => void;
  isSidebarClosed?: boolean;
}

const HeaderSection: React.FC<HeaderProps> = ({ onMenuClick, isSidebarClosed }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const userDetails = useAppSelector((state) => state.authReducer.loginDetials);
  const isCustomerPortal = userDetails?.role === "User";

  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [supportAnchorEl, setSupportAnchorEl] = useState<null | HTMLElement>(null);
  const [currentLang, setCurrentLang] = useState<string>(i18n.language || "en");
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletCurrency, setWalletCurrency] = useState("IQD");
  const [walletLoading, setWalletLoading] = useState(false);

  const { balanceVisible, toggleBalanceVisible } = useWalletBalanceVisibility();

  const langOpen = Boolean(langAnchorEl);
  const supportOpen = Boolean(supportAnchorEl);
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  const formattedDate = new Date().toLocaleDateString(
    currentLang === "ar" ? "ar-SA" : "en-US",
    {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );

  const fetchWalletBalance = useCallback(async () => {
    if (!isCustomerPortal) return;
    setWalletLoading(true);
    try {
      const response = await dashboardServices.getBalanceSummary();
      const balances = normalizeBalanceSummaryResponse(response?.data);
      setWalletBalance(balances.appWalletBalance);
      setWalletCurrency(balances.currency);
    } catch {
      /* keep last known balance */
    } finally {
      setWalletLoading(false);
    }
  }, [isCustomerPortal]);

  useEffect(() => {
    const savedLang = localStorage.getItem("i18nextLng") || "en";
    setCurrentLang(savedLang);
  }, [i18n.language]);

  useEffect(() => {
    if (!isCustomerPortal) return;
    fetchWalletBalance();
    return onPaymentsEvent(fetchWalletBalance);
  }, [isCustomerPortal, fetchWalletBalance]);

  const maskedBalanceDisplay = useMemo(() => {
    const prefix = walletCurrency?.trim()
      ? `${walletCurrency.trim()} `
      : `${CURRENCY_SYMBOL.trim()} `;
    return `${prefix}••••••••`;
  }, [walletCurrency]);

  const getPageTitle = () => {
    switch (location.pathname) {
      case "/dashboard":
        return t("header.customerPortal");
      case "/saleOrder":
        return t("header.salesOrder");
      case "/invoicesPayments":
        return t("header.invoicesPayments");
      case "/deliveryManagement":
        return t("header.deliveryManagement");
      case "/returnsManagement":
        return t("header.returns");
      case "/CustomerEnrolment":
        return t("header.customerEnrolment");
      case "/workflowPortal":
        return t("header.workflowPortal");
      case "/SalesRepEnrolment":
        return t("header.salesRepEnrolment");
      case "/SalesManagerEnrolment":
        return t("header.salesManagerEnrolment");
      case "/itemControlRelease":
        return t("header.itemControlRelease");
      case "/inventoryReconciliation":
        return t("header.inventoryReconciliation");
      case "/supervisorReports":
        return t("header.supervisorReports");
      case "/whmReports":
        return t("header.whmReports");
      case "/codSalesRep":
        return t("header.codSalesRep");
      case "/codSalesManager":
        return t("header.codSalesManager");
      case "/codSalesRepReports":
      case "/codSalesManagerReports":
        return t("header.codReports");
      case "/walletBalance":
        return t("header.walletBalance");
      default:
        return t("header.customerPortal");
    }
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("i18nextLng", lang);
    setCurrentLang(lang);
    applyDocumentDirection(lang);
    handleLanguageClose();
  };

  const languages = [
    { code: "en", label: "GB English" },
    { code: "ar", label: "SA (Arabic) العربية" },
  ];

  return (
    <>
      <Box
        position="static"
        sx={{
          background: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: `1px solid ${NJIM.border}`,
          boxShadow: NJIM.elevation,
          padding: 0,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1.5,
            px: { xs: 2.5, sm: 4, lg: 5 },
            minHeight: 64,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <IconButton
              onClick={onMenuClick}
              className="menu-icon"
              sx={{
                marginInlineEnd: 1.5,
                p: 0.8,
                borderRadius: "8px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(22,45,58,0.06)",
                },
              }}
            >
              {isSidebarClosed ? (
                <MenuIcon sx={{ fontSize: 22, color: NJIM.textPrimary, transition: "transform 0.3s ease" }} />
              ) : (
                <MenuOpenIcon sx={{ fontSize: 22, color: NJIM.ink, transition: "transform 0.3s ease" }} />
              )}
            </IconButton>

            <Typography
              sx={{
                fontWeight: 600,
                color: NJIM.textPrimary,
                fontSize: "15px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getPageTitle()}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5, md: 2 }, flexShrink: 0 }}>
            {isCustomerPortal && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: { xs: 1, md: 1.5 },
                  py: 0.65,
                  borderRadius: "999px",
                  border: `1px solid ${NJIM.border}`,
                  bgcolor: NJIM.white,
                  boxShadow: "0 1px 4px rgba(22,45,58,0.06)",
                  maxWidth: { xs: 160, sm: 220, md: 280 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    color: NJIM.textSecondary,
                    fontWeight: 500,
                    flexShrink: 0,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {t("header.wallet")}
                </Typography>
                {walletLoading ? (
                  <CircularProgress size={16} sx={{ color: NJIM.ink, mx: 0.5 }} />
                ) : (
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: NJIM.textPrimary,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {balanceVisible
                      ? formatWalletDisplay(walletBalance, walletCurrency)
                      : maskedBalanceDisplay}
                  </Typography>
                )}
                <Tooltip
                  title={
                    balanceVisible ? t("walletPage.hideBalance") : t("walletPage.showBalance")
                  }
                >
                  <IconButton
                    size="small"
                    onClick={toggleBalanceVisible}
                    aria-label={
                      balanceVisible ? t("walletPage.hideBalance") : t("walletPage.showBalance")
                    }
                    sx={{
                      marginInlineStart: 0.25,
                      color: NJIM.textSecondary,
                      "&:hover": { color: NJIM.ink, bgcolor: NJIM.mintWash },
                    }}
                  >
                    {balanceVisible ? (
                      <VisibilityOffIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <Typography
              sx={{
                color: NJIM.textSecondary,
                fontSize: "14px",
                display: { xs: "none", lg: "block" },
                whiteSpace: "nowrap",
              }}
            >
              {formattedDate}
            </Typography>

            {isCustomerPortal && (
              <>
                <Button
                  onClick={(e) => setSupportAnchorEl(e.currentTarget)}
                  {...rtlIconButtonProps(
                    isRTL,
                    <HeadsetMicOutlinedIcon sx={{ fontSize: 18 }} />
                  )}
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 14,
                    px: 2,
                    py: 0.75,
                    minHeight: 36,
                    color: NJIM.white,
                    bgcolor: NJIM.ink,
                    boxShadow: NJIM.elevation,
                    ...RTL_ICON_BUTTON_SX,
                    "&:hover": {
                      bgcolor: NJIM.info,
                      boxShadow: NJIM.elevationLg,
                    },
                  }}
                >
                  {t("header.support")}
                </Button>

                <IconButton
                  onClick={(e) => setSupportAnchorEl(e.currentTarget)}
                  aria-label={t("header.support")}
                  sx={{
                    display: { xs: "inline-flex", sm: "none" },
                    bgcolor: NJIM.ink,
                    color: NJIM.white,
                    width: 36,
                    height: 36,
                    "&:hover": { bgcolor: NJIM.info },
                  }}
                >
                  <HeadsetMicOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                <Popover
                  open={supportOpen}
                  anchorEl={supportAnchorEl}
                  onClose={() => setSupportAnchorEl(null)}
                  {...popoverOriginEnd(isRTL)}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1.5,
                        width: { xs: "min(340px, calc(100vw - 32px))", sm: 360 },
                        p: 2.5,
                        borderRadius: "16px",
                        border: `1px solid ${NJIM.border}`,
                        boxShadow: NJIM.elevationLg,
                      },
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: NJIM.textPrimary, mb: 0.5 }}>
                    {t("header.customerSupport")}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, mb: 2 }}>
                    {t("header.supportHours")}
                  </Typography>

                  <SupportContactLinks variant="popover" />

                  <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mt: 2, lineHeight: 1.5 }}>
                    {t("header.supportHint")}
                  </Typography>
                </Popover>
              </>
            )}

            {/* Language toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                padding: "6px 10px",
                borderRadius: "999px",
                border: "1px solid rgba(22,45,58,0.12)",
                background: NJIM.white,
                boxShadow: NJIM.elevation,
                transition: "background-color 180ms ease, border-color 180ms ease",
                "&:hover": {
                  background: NJIM.inkSoft,
                  borderColor: "rgba(22,45,58,0.2)",
                  boxShadow: "0 4px 12px rgba(22,45,58,0.08)",
                },
              }}
              onClick={handleLanguageClick}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  minWidth: 0,
                }}
              >
                <TranslateOutlinedIcon
                  sx={{
                    fontSize: "18px",
                    color: NJIM.ink,
                    background: NJIM.inkSoft,
                    borderRadius: "50%",
                    padding: "4px",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    color: NJIM.textPrimary,
                    fontSize: "14px",
                    fontWeight: 600,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {currentLang === "en" ? "English" : "العربية"}
                </Typography>
              </Box>
              <KeyboardArrowDownRoundedIcon
                sx={{
                  fontSize: 18,
                  color: NJIM.textSecondary,
                  flexShrink: 0,
                  transition: "transform 0.2s ease",
                  transform: langOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Box>

            <Menu
              anchorEl={langAnchorEl}
              open={langOpen}
              onClose={handleLanguageClose}
              {...popoverOriginEnd(isRTL)}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 180,
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  background: "rgba(255,255,255,0.95)",
                  boxShadow: "0 16px 36px rgba(2,8,23,0.12), 0 2px 8px rgba(2,8,23,0.05)",
                  borderRadius: "14px",
                  border: `1px solid ${NJIM.border}`,
                  p: 0.5,
                },
              }}
            >
              {languages.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  selected={currentLang === lang.code}
                  sx={{
                    padding: "9px 12px",
                    fontSize: "14px",
                    backgroundColor:
                      currentLang === lang.code ? NJIM.inkSoft : "transparent",
                    color: currentLang === lang.code ? NJIM.ink : NJIM.textPrimary,
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor:
                        currentLang === lang.code ? NJIM.inkSoft : NJIM.muted,
                    },
                    "&.Mui-selected": {
                      backgroundColor: `${NJIM.inkSoft} !important`,
                      color: NJIM.ink,
                      "&:hover": {
                        backgroundColor: `${NJIM.inkSoft} !important`,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: currentLang === lang.code ? 600 : 400,
                        }}
                      >
                        {lang.label}
                      </Typography>
                    </Box>
                    {currentLang === lang.code && (
                      <CheckIcon
                        sx={{
                          fontSize: "18px",
                          color: NJIM.ink,
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Box>
    </>
  );
};

export default HeaderSection;
